import { useState } from 'react';
import api from '../services/api';
import useLang from '../hooks/useLang';

export default function RegisterGroupModal({ onClose }) {
  const { T } = useLang();
  const R = T.registerGroup;

  const [form, setForm] = useState({
    group_name: '',
    member_count: '',
    chair_name: '',
    chair_phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/register-group', {
        ...form,
        member_count: parseInt(form.member_count, 10),
      });
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (err.response?.status === 409) setError(R.errDuplicate);
      else if (err.response?.status === 400) setError(R.errRequired);
      else setError(msg || R.errGeneric);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent focus:bg-white transition-all';
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-white shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{R.title}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{R.subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-6 py-5">
          {success ? (
            <div className="text-center py-8">
              <div className="text-5xl mb-4">🎉</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{R.successTitle}</h3>
              <p className="text-sm text-gray-500 mb-6">{R.successMsg}</p>
              <button
                onClick={onClose}
                className="w-full rounded-xl py-3 text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg,#059669,#0d9488)' }}
              >
                {R.btnClose}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                  <span>⚠️</span><span>{error}</span>
                </div>
              )}

              <div>
                <label className={labelCls}>{R.labelGroupName}</label>
                <input
                  type="text"
                  value={form.group_name}
                  onChange={set('group_name')}
                  placeholder={R.placeholderGroupName}
                  required
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>{R.labelMemberCount}</label>
                <input
                  type="number"
                  value={form.member_count}
                  onChange={set('member_count')}
                  placeholder={R.placeholderMemberCount}
                  min="2"
                  required
                  className={inputCls}
                />
              </div>

              <div className="pt-1">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-3">
                  {R.sectionChair}
                </p>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>{R.labelChairName}</label>
                    <input
                      type="text"
                      value={form.chair_name}
                      onChange={set('chair_name')}
                      placeholder={R.placeholderChairName}
                      required
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>{R.labelChairPhone}</label>
                    <input
                      type="tel"
                      value={form.chair_phone}
                      onChange={set('chair_phone')}
                      placeholder="0700000000"
                      required
                      className={inputCls}
                    />
                    <p className="mt-1 text-xs text-gray-400">{R.phoneHint}</p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl py-3.5 text-sm font-bold text-white mt-2 transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg,#059669,#0d9488)',
                  boxShadow: loading ? 'none' : '0 4px 15px rgba(5,150,105,0.4)',
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    {R.btnSubmitting}
                  </span>
                ) : R.btnSubmit}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
